import type { ExtractProfileFromDocumentMutation } from "@/graphql/generated/operations";
import { actionableItems } from "@/stores/profile-merge-store";
import type { DiffAction, PreviewData, RowDiff, RowFieldDiff, RowFieldKind } from "./profile-merge-types";

// The genesis `ProfileImportPreview` payload for a CV extraction — the SAME two-sided diff the
// LinkedIn importer returns (genesis unified both onto one diff engine). genesis has already
// compared the extraction against the caller's existing profile, resolved reference fields to ids,
// matched collection rows and flagged additive citizenship, so the client just adapts the GraphQL
// shape (unwrapping each CollectionDiff's `rows`) into PreviewData — no existing-side
// reconstruction, reference resolution, or date normalization here anymore.
export type CvImportPreview = ExtractProfileFromDocumentMutation["extractProfileFromDocument"];

type GqlCollectionDiff = CvImportPreview["experiences"];

function toRows(collection: GqlCollectionDiff): RowDiff[] {
  return collection.rows.map((r) => ({
    rowKey: r.rowKey,
    action: r.action as DiffAction,
    existingRowId: r.existingRowId,
    fields: r.fields.map(
      (f): RowFieldDiff => ({
        field: f.field,
        existingValue: f.existingValue,
        incomingValue: f.incomingValue,
        kind: f.kind as RowFieldKind,
        editable: f.editable,
      }),
    ),
  }));
}

/** Adapts a genesis CV `ProfileImportPreview` into the merge store's `PreviewData` (tagged `cv`). */
export function cvExtractionToPreview(preview: CvImportPreview): PreviewData {
  return {
    source: "cv",
    personDetail: preview.personDetail.map((d) => ({
      key: d.key,
      field: d.field,
      existingValue: d.existingValue,
      incomingValue: d.incomingValue,
      action: d.action as DiffAction,
      additive: d.additive,
    })),
    experiences: toRows(preview.experiences),
    educations: toRows(preview.educations),
    languageSkills: toRows(preview.languageSkills),
    certificates: toRows(preview.certificates),
    expertises: toRows(preview.expertises),
  };
}

/**
 * Whether the extraction has anything worth reviewing — drives the CV dialog's "review changes" vs
 * "nothing new" branch. This is now the SAME authoritative check the profile page uses to open the
 * review (`actionableItems` over the adapted preview), since genesis already produced the real diff
 * — no more coarse dialog-vs-page divergence.
 */
export function hasExtractedProposals(preview: CvImportPreview): boolean {
  return actionableItems(cvExtractionToPreview(preview)).length > 0;
}
